import { Change, diffChars, diffLines, diffSentences, diffWords } from 'diff';
import { useCallback, useMemo, useState } from 'react';

type Method = 'sentences' | 'lines' | 'words' | 'chars';

interface Options {
    method?: Method;
}

export default function useDiffAsync(options?: Options) {
    const method = options?.method ?? 'words';
    const [isComputing, setIsComputing] = useState(false);

    const analyze = useMemo(
        () => ({ sentences: diffSentences, lines: diffLines, words: diffWords, chars: diffChars }[method]),
        [method]
    );

    const diff = useCallback(
        (oldText: string, newText: string) => {
            setIsComputing(true);
            return new Promise<Change[]>((resolve, reject) => {
                analyze(oldText, newText, {
                    ignoreCase: true,
                    callback: (error, changes) => {
                        switch (true) {
                            case !!error:
                                reject(error);
                                break;
                            case !changes:
                                reject('Failed to calculate diff');
                                break;
                            default:
                                resolve(changes);
                                break;
                        }
                    }
                });
            }).finally(() => {
                setIsComputing(false);
            });
        },
        [analyze]
    );

    return [diff, isComputing] as const;
}
